import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useStorage } from '@vueuse/core';
import { castArray, merge, assign, pick } from 'lodash-es';
import uniqid from 'uniqid';
import {
  NotificationType,
  NOTIFICATIONS_CONFIG,
  TASK_STATE_TO_NOTIFICATION_TYPE_MAP,
  VHS_TASK_STATUS_TO_NOTIFICATION_TYPE_MAP
} from '@/common/constants/notifications.constants';
import Task from '@/common/models/task.model';
import jsonParseSafe from '@/common/utils/jsonParseSafe';
import { getTimestamp } from '@/common/utils/date';
import { BackendTask } from '@strg/pstorage-ui-backend-schema/dist/types';
import { TASK_MAP } from '@/common/store/modules/tasks/tasksNotificationMapper';
import type { Location } from 'vue-router';
import type { VhsTask } from '@/common/store/pinia/vhsTasks';
import { trackDebounced } from '@/common/telemetry';

export type NotificationComponent = {
  name: string;
  attrs?: Record<string, unknown>;
  listeners?: Record<string, unknown>;
  content?: string;
};

export type NotificationAction = {
  name: string;
  label: string;
  display?: boolean;
  route?: Location;
  payload?: Record<string, unknown>;
};

export type NotificationItem = {
  id: string;
  timestamp: number;
  isSeen: boolean;
  showBottom?: boolean;
  hasMessage?: boolean;
  task?: (BackendTask & { finished?: string }) | VhsTask;
  progress?: number;
  components: NotificationComponent[];
  actions?: NotificationAction[];
  type: string;
  title?: string;
  message?: string;
  duration?: number;
  showClose?: boolean;
  skipStore?: boolean;
  preventReload?: boolean;
  operationId?: string;
  isVhsTask?: boolean;
  inProgressItems?: { id: string; name: string; icon?: string; statusIcon?: string; statusText?: string }[];
  onClose?: () => void;
}

export type NotificationState = {
  version: number;
  muted: Record<string, boolean>;
  maxBottomItems: number;
  maxMainItems: number;
  notifications: NotificationItem[];
  requestedTasks: string[];
};

export const getInitialConfig = () => ({
  [NotificationType.ALL]: false,
  [NotificationType.ERROR]: false,
  [NotificationType.INFO]: false,
  [NotificationType.SUCCESS]: false
});

export const createInitialState = (): NotificationState => ({
  version: 2,
  muted: getInitialConfig(),
  maxBottomItems: 3,
  maxMainItems: 30,
  notifications: [],
  requestedTasks: []
});

const trackNotification = (notification: NotificationItem) => {
  trackDebounced('notification', pick(notification, ['id', 'timestamp', 'type', 'operationId', 'title', 'message']));
};

const getUpdatedTimestamp = (notification: Partial<NotificationItem>, existingNotification: NotificationItem) => {
  const { task } = notification;
  if (task && 'finished' in task && task.finished) {
    try {
      return getTimestamp(task.finished);
    } catch (error) {
      return Date.now();
    }
  }
  return existingNotification.timestamp;
};

export const useNotificationsStore = defineStore('notifications', () => {
  const getInitialState = () => {
    const localStorageString = localStorage.getItem('notifications');
    const storage = localStorageString ? jsonParseSafe(localStorageString) as NotificationState : undefined;
    const initialState = createInitialState();

    if (storage?.version !== initialState.version) {
      localStorage.removeItem('notifications');
    }

    return useStorage(
      'notifications',
      initialState,
      localStorage,
      {
        mergeDefaults: (storageValue, defaults) => merge(defaults, {
          ...storageValue,
          notifications: storageValue.notifications.filter(it => !it.skipStore && it.type !== NotificationType.PROGRESS)
        }),
        writeDefaults: false
      }
    ).value;
  };

  const state = ref<NotificationState>(getInitialState());

  const getNotificationConfig = (task: Task) => {
    const config = TASK_MAP[task.name]?.[task.state];
    return config && config(task);
  };

  // Getters
  const doNotDisturb = computed(() => state.value.muted[NotificationType.ALL]);

  const isMuted = computed(() => (type: string) => state.value.muted[NotificationType.ALL] || state.value.muted[type]);

  const getItems = computed(() => state.value.notifications.map(notification => {
    if (notification.task) {
      try {
        const taskModel = new Task(notification.task);
        const config = getNotificationConfig(taskModel);
        return {
          ...notification,
          ...config,
          hasMessage: !!config
        };
      } catch (error) {
        console.error('Error while processing task notification', error);
        return notification;
      }
    }
    return notification;
  }).slice().sort((a, b) => b.timestamp - a.timestamp));

  const getItemById = computed(() => (id: string) => state.value.notifications.find(it => it.id === id));

  const tasksNotifications = computed(() => getItems.value
    .filter(it => it.task)
    .map(notification => ({
      ...notification,
      title: undefined,
      message: notification.task.name
    })));

  const mainNotifications = computed(() => getItems.value.filter(it => it.task ? it.hasMessage : true));

  const bottomNotifications = computed(() => getItems.value.filter(it => {
    const shouldShow = it.showBottom && !isMuted.value(it.type);
    if (it.task) {
      return state.value.requestedTasks.includes(it.id) && it.hasMessage && shouldShow;
    }
    return shouldShow;
  }).slice(-1 * state.value.maxBottomItems));

  const preventReloadProgressNotifications = computed(() => getItems.value.filter(it => it.type === NotificationType.PROGRESS && it.preventReload));

  const removeNotification = (id: string | string[]) => {
    const ids = castArray(id);
    state.value.notifications = state.value.notifications.filter(notification => !ids.includes(notification.id));
  };

  const updateNotification = (newNotification: Partial<NotificationItem>) => {
    assign(
      state.value.notifications.find(notification => notification.id === newNotification.id),
      newNotification
    );
  };

  const muteNotifications = (types: Record<string, boolean>) => {
    Object.assign(state.value.muted, types);
  };

  const $reset = () => {
    const initialState = createInitialState();
    Object.assign(state.value, initialState);
  };

  const addTaskId = (taskId: string) => {
    const { requestedTasks } = state.value;
    if (!requestedTasks.includes(taskId)) {
      requestedTasks.push(taskId);
    }
  };

  const removeTaskIds = (id: string | string[]) => {
    const ids = castArray(id);
    state.value.requestedTasks = state.value.requestedTasks.filter(taskId => !ids.includes(taskId));
  };

  const add = (notification: any) => {
    const existingNotification = getItemById.value(notification.id);
    if (existingNotification) return;

    const notificationType = notification.type || NotificationType.INFO;
    const progress = notification.type === NotificationType.PROGRESS ? notification.progress : undefined;
    const { task } = notification;
    const timestamp = task?.finished ? getTimestamp(task.finished) : Date.now();
    const showBottom = 'showBottom' in notification ? notification.showBottom : true;

    const updatedNotification: NotificationItem = {
      ...NOTIFICATIONS_CONFIG[notificationType],
      id: uniqid('notification_'),
      timestamp,
      isSeen: false,
      type: notificationType,
      components: [],
      ...notification,
      showBottom,
      progress
    };

    state.value.notifications = [
      ...state.value.notifications,
      updatedNotification
    ].slice(-1 * state.value.maxMainItems);
    trackNotification(updatedNotification);
  };

  const update = (notification: Partial<NotificationItem>) => {
    const existingNotification = notification.id ? getItemById.value(notification.id) : undefined;
    if (!existingNotification) return;

    const notificationType = notification.type || existingNotification.type;
    const isInProgress = notificationType === NotificationType.PROGRESS;
    const progress = isInProgress ? notification.progress : undefined;
    const isDifferentNotificationType = notification.task && existingNotification.type !== notification.type;
    const showBottom = 'showBottom' in notification
      ? notification.showBottom
      : (existingNotification.showBottom || isDifferentNotificationType);
    const notificationDefaultConfig = NOTIFICATIONS_CONFIG[notificationType];

    const updatedNotification = {
      ...existingNotification,
      ...notificationDefaultConfig,
      ...notification,
      showBottom,
      progress,
      timestamp: getUpdatedTimestamp(notification, existingNotification)
    };

    trackNotification(updatedNotification);
    updateNotification(updatedNotification);

    const notificationId = notification.id;

    if (!isInProgress && typeof notificationId === 'string') {
      setTimeout(
        () => removeTaskIds(notificationId),
        notificationDefaultConfig.duration
      );
    }
  };

  const addOrUpdate = (notification: any) => {
    const existingNotification = getItemById.value(notification.id);
    return existingNotification
      ? update(notification)
      : add(notification);
  };

  const processWsTask = (task: BackendTask) => {
    addOrUpdate({
      id: task.task_id,
      operationId: task.task_id,
      type: TASK_STATE_TO_NOTIFICATION_TYPE_MAP[task.state],
      message: task.name,
      progress: task.progress,
      task
    });
  };

  const revealTask = (task: BackendTask) => {
    addOrUpdate({
      id: task.task_id,
      operationId: task.task_id,
      type: TASK_STATE_TO_NOTIFICATION_TYPE_MAP[task.state],
      message: task.name,
      progress: task.progress,
      showBottom: false,
      task
    });
  };

  const revealVhsTask = (vhsTask: VhsTask) => {
    addOrUpdate({
      id: vhsTask.task_id,
      operationId: vhsTask.task_id,
      type: VHS_TASK_STATUS_TO_NOTIFICATION_TYPE_MAP[vhsTask.status],
      message: vhsTask.message ?? undefined,
      progress: vhsTask.progress,
      timestamp: getTimestamp(vhsTask.updated_at),
      showBottom: false,
      isVhsTask: true,
      task: vhsTask
    });
  };

  const trackTask = (taskId: string) => {
    addTaskId(taskId);
  };

  const remove = (id: string | string[]) => {
    removeNotification(id);
    removeTaskIds(id);
  };

  const dismiss = (id: string) => {
    const notification = getItemById.value(id);
    if (!notification) return;

    updateNotification({
      id,
      showBottom: false,
    });
  };

  const setSeenNotifications = () => {
    state.value.notifications.forEach(notification => updateNotification({
      id: notification.id,
      isSeen: notification.type !== NotificationType.PROGRESS
    }));
  };

  const mute = (types: Record<string, boolean>) => {
    muteNotifications(types);
  };

  const unmute = () => {
    muteNotifications(getInitialConfig());
  };

  return {
    // State
    state,
    // Getters
    doNotDisturb,
    isMuted,
    getItems,
    getItemById,
    tasksNotifications,
    mainNotifications,
    bottomNotifications,
    preventReloadProgressNotifications,
    // Actions
    processWsTask,
    revealTask,
    add,
    update,
    addOrUpdate,
    trackTask,
    remove,
    dismiss,
    setSeenNotifications,
    mute,
    unmute,
    $reset,
    revealVhsTask
  };
});
